import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, X, CheckCircle } from "@phosphor-icons/react";

const emptyForm = () => ({
    board: "kpsc", exam_id: "", subject: "General Studies", content: "",
    options: ["", "", "", ""], correct_index: 0, explanation: "",
    from_pyq: false, year: null, difficulty: "medium",
});

export default function AdminQuestions() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [board, setBoard] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/questions", { params: { q, board } });
            setItems(data.items); setTotal(data.total);
        } catch (e) { toast.error("Failed to load questions"); }
        finally { setLoading(false); }
    }, [q, board]);

    useEffect(() => { load(); }, [load]);

    const openNew = () => { setEditing(null); setForm(emptyForm()); };
    const openEdit = (item) => {
        setEditing(item.question_id);
        setForm({
            board: item.board || "kpsc",
            exam_id: item.exam_id || "",
            subject: item.subject || "General Studies",
            content: item.content || item.question || "",
            options: (item.options && item.options.length === 4) ? item.options : ["", "", "", ""],
            correct_index: item.correct_index ?? 0,
            explanation: item.explanation || "",
            from_pyq: !!item.from_pyq,
            year: item.year || null,
            difficulty: item.difficulty || "medium",
        });
    };

    const save = async () => {
        if (!form.content.trim() || form.options.some((o) => !o.trim())) {
            toast.error("Question + all 4 options are required"); return;
        }
        try {
            if (editing) {
                await api.put(`/admin/questions/${editing}`, form);
                toast.success("Question updated");
            } else {
                await api.post("/admin/questions", form);
                toast.success("Question created");
            }
            setEditing(null); setForm(emptyForm()); await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this question?")) return;
        try { await api.delete(`/admin/questions/${id}`); toast.success("Deleted"); await load(); }
        catch (e) { toast.error("Delete failed"); }
    };

    const isFormOpen = editing !== null || form.content || form.options.some(o => o);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        data-testid="q-search"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search question text…"
                        className="w-full pl-9 pr-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A]"
                    />
                </div>
                <select data-testid="q-board" value={board} onChange={(e) => setBoard(e.target.value)} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                    <option value="">All boards</option>
                    <option value="kpsc">KPSC</option>
                    <option value="ksp">KSP</option>
                    <option value="kea">KEA</option>
                </select>
                <button data-testid="q-new" onClick={openNew} className="ml-auto btn-primary !py-2 !px-4"><Plus size={16} weight="bold" /> New Question</button>
            </div>

            {/* Editor */}
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold">{editing ? "Edit question" : "New question"}</h3>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyForm()); }} className="text-xs text-slate-500 flex items-center gap-1"><X size={12} /> Cancel edit</button>}
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                    <select value={form.board} onChange={(e) => setForm({ ...form, board: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                        <option value="kpsc">KPSC</option><option value="ksp">KSP</option><option value="kea">KEA</option>
                    </select>
                    <input placeholder="Exam ID (e.g. kas)" value={form.exam_id} onChange={(e) => setForm({ ...form, exam_id: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                        <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                    </select>
                </div>
                <textarea
                    data-testid="q-content"
                    placeholder="Question text"
                    value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                    rows={2} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y"
                />
                <div className="grid md:grid-cols-2 gap-3">
                    {form.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <button onClick={() => setForm({ ...form, correct_index: i })} title="Mark as correct" className={`w-8 h-8 rounded-full border-2 grid place-items-center text-xs font-bold ${form.correct_index === i ? "bg-emerald-100 border-emerald-500 text-emerald-800" : "border-slate-300 text-slate-500"}`}>
                                {String.fromCharCode(65 + i)}
                            </button>
                            <input
                                data-testid={`q-opt-${i}`}
                                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                                value={opt}
                                onChange={(e) => {
                                    const opts = [...form.options]; opts[i] = e.target.value;
                                    setForm({ ...form, options: opts });
                                }}
                                className="flex-1 px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm"
                            />
                        </div>
                    ))}
                </div>
                <textarea placeholder="Explanation (optional)" value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <div className="flex items-center gap-3 text-xs">
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.from_pyq} onChange={(e) => setForm({ ...form, from_pyq: e.target.checked })} /> From PYQ</label>
                    <input type="number" placeholder="Year" value={form.year || ""} onChange={(e) => setForm({ ...form, year: e.target.value ? parseInt(e.target.value) : null })} className="w-24 px-2 py-1 border-2 border-[#BFD3FF] rounded-md" />
                    <button data-testid="q-save" onClick={save} className="ml-auto btn-primary !py-2 !px-4"><CheckCircle size={16} weight="fill" /> {editing ? "Update" : "Create"}</button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b-2 border-[#BFD3FF] text-xs uppercase tracking-widest font-bold text-slate-600 flex justify-between">
                    <span>{loading ? "Loading…" : `${items.length} of ${total} questions`}</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                                <th className="px-4 py-2 font-bold">Board</th>
                                <th className="px-4 py-2 font-bold">Subject</th>
                                <th className="px-4 py-2 font-bold">Question</th>
                                <th className="px-4 py-2 font-bold">PYQ</th>
                                <th className="px-4 py-2 font-bold w-24 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((q) => (
                                <tr key={q.question_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-3 uppercase text-xs font-bold text-[#1E3A8A]">{q.board}</td>
                                    <td className="px-4 py-3 text-xs">{q.subject}</td>
                                    <td className="px-4 py-3 text-xs text-slate-700 max-w-md truncate">{q.content || q.question}</td>
                                    <td className="px-4 py-3 text-[10px]">{q.from_pyq ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">{q.year || "PYQ"}</span> : "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => openEdit(q)} data-testid={`q-edit-${q.question_id}`} className="p-1.5 text-slate-500 hover:text-[#1E3A8A]" title="Edit"><PencilSimple size={14} /></button>
                                        <button onClick={() => remove(q.question_id)} data-testid={`q-del-${q.question_id}`} className="p-1.5 text-slate-500 hover:text-red-600" title="Delete"><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (
                                <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">No questions found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
